import React, { useMemo, useState } from 'react';
import { cmp, latent, alertes, TYPES } from '../livre/index.js';
import { eur, cfa, taux as fmtTaux } from '../format.js';
import Info from '../composants/Info.jsx';
import Segmente from '../composants/Segmente.jsx';

/**
 * Écran Position - le livre. Document 04, écran 2.
 *
 * Vue de pilotage : où en sont mes trésoreries, mon stock, ma marge. Aucun
 * calcul ici (AD-3) : tout vient du moteur de position.
 */
export default function Position({ livre, journal, reglages, onMouvement, onSupprimer }) {
  const [tauxRef, setTauxRef] = useState(680);
  const [filtreType, setFiltreType] = useState('tous');
  const [saisie, setSaisie] = useState(false);

  const { valeurLatenteCfa, pnlLatentCfa } = latent(livre, tauxRef);
  const seuils = {
    caisseEur: reglages.seuilCaisseEur ?? 0,
    caisseXaf: reglages.seuilCaisseXaf ?? 0,
    caisseXof: reglages.seuilCaisseXof ?? 0,
    // `null` et 0 ne disent pas la même chose : 0 alerterait au moindre euro
    // porté, alors que l'absence de seuil doit n'alerter jamais.
    expositionEur: (reglages.seuilExpoEur ?? null) === null ? Infinity : reglages.seuilExpoEur,
  };
  const levees = alertes(livre, seuils);

  const visible = useMemo(
    () => (filtreType === 'tous' ? journal : journal.filter((e) => e.mouvement.type === filtreType)),
    [journal, filtreType],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--espace-5)' }}>
      {levees.length > 0 && <Alertes levees={levees} />}

      <section>
        <h2 style={titre}>Trésoreries</h2>
        <div className="grille-3">
          <Caisse libelle="Europe" valeur={eur(livre.caisseEur)} sousSeuil={livre.caisseEur < seuils.caisseEur} />
          <Caisse libelle="CEMAC" valeur={cfa(livre.caisseXaf, 'XAF')} sousSeuil={livre.caisseXaf < seuils.caisseXaf} />
          <Caisse libelle="UEMOA" valeur={cfa(livre.caisseXof, 'XOF')} sousSeuil={livre.caisseXof < seuils.caisseXof} />
        </div>
      </section>

      <section>
        <h2 style={titre}>
          Position euro
          <Info titre="Coût moyen pondéré">
            Le prix de revient d’un euro en stock, toutes acquisitions
            confondues. Un achat le déplace ; une vente sort le stock à ce coût
            et le laisse inchangé. C’est lui qui sert de référence à chaque
            marge réalisée.
          </Info>
        </h2>

        <div className="carte" style={{ display: 'grid', gap: 'var(--espace-3)' }}>
          <div className="grille-2">
            <Grandeur libelle="Euros portés" valeur={eur(livre.stockEur)} />
            <Grandeur libelle="Coût moyen pondéré" valeur={`${fmtTaux(cmp(livre))} F/€`} />
          </div>

          <label style={{ display: 'block' }}>
            <span className="etiquette">
              Taux de revente de référence
              <Info titre="Marge latente">
                Ce que vaudrait votre stock si vous le revendiez à ce taux. Elle
                n’est <strong>pas acquise</strong> : elle dépend d’un taux futur
                et de la demande. Elle mesure une exposition, pas un gain.
              </Info>
            </span>
            <input
              type="number"
              step="1"
              value={tauxRef}
              onChange={(e) => setTauxRef(Number(e.target.value))}
              className="champ"
              style={{ marginTop: 'var(--espace-1)' }}
            />
          </label>

          <div className="grille-2">
            <Grandeur libelle="Valeur au taux de référence" valeur={cfa(valeurLatenteCfa, 'F')} />
            <Grandeur
              libelle="Marge latente"
              valeur={cfa(pnlLatentCfa, 'F')}
              couleur={pnlLatentCfa < 0 ? 'var(--couleur-alerte)' : undefined}
            />
          </div>
        </div>
      </section>

      <section>
        <h2 style={titre}>Marge réalisée</h2>
        {/* Carte sombre, comme la marge d'une cotation : ce que garde la
            plateforme se lit d'un coup d'œil, distinct des chiffres client. */}
        <div
          className="carte"
          style={{ background: 'var(--couleur-encre)', borderColor: 'var(--couleur-encre)', color: 'var(--couleur-sur-encre)' }}
        >
          <div className="etiquette" style={{ color: 'var(--couleur-sur-encre-douce)' }}>Cumul encaissé</div>
          <div
            className="chiffre"
            style={{
              fontSize: '1.5rem', fontWeight: 700, margin: 'var(--espace-1) 0 var(--espace-3)',
              color: livre.pnlRealiseCfa < 0 ? 'var(--couleur-alerte-claire)' : 'var(--couleur-emphase)',
            }}
          >
            {cfa(livre.pnlRealiseCfa, 'F')}
          </div>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--espace-1) var(--espace-3)', margin: 0, fontSize: '0.875rem' }}>
            {['XAF', 'XOF'].map((c) => (
              <React.Fragment key={c}>
                <dt style={{ color: 'var(--couleur-sur-encre-douce)' }}>Corridor {c}</dt>
                <dd className="chiffre" style={{ margin: 0, textAlign: 'right' }}>
                  {cfa(livre.margeParCorridor[c], 'F')}
                </dd>
              </React.Fragment>
            ))}
          </dl>
          <p style={{ margin: 'var(--espace-3) 0 0', fontSize: '0.8rem', color: 'var(--couleur-sur-encre-douce)' }}>
            Ventilation indicative : le stock d’euros est commun, le corridor
            n’est qu’une étiquette posée à la vente.
          </p>
        </div>
      </section>

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--espace-3)', flexWrap: 'wrap' }}>
          <h2 style={{ ...titre, borderBottom: 'none', paddingBottom: 0 }}>Journal des mouvements</h2>
          <button
            type="button"
            onClick={() => setSaisie((s) => !s)}
            aria-expanded={saisie}
            style={boutonSecondaire}
          >
            {saisie ? 'Annuler' : 'Mouvement manuel'}
          </button>
        </div>

        {saisie && (
          <MouvementManuel
            onValider={async (m) => { await onMouvement(m); setSaisie(false); }}
            onAnnuler={() => setSaisie(false)}
          />
        )}

        <div style={{ margin: 'var(--espace-3) 0' }}>
          <Segmente
            etiquette="Filtrer"
            compact
            valeur={filtreType}
            onChange={setFiltreType}
            options={[
              { valeur: 'tous', libelle: 'Tous' },
              { valeur: 'achat_eur', libelle: 'Achats' },
              { valeur: 'vente_eur', libelle: 'Ventes' },
              { valeur: 'frais_service', libelle: 'Frais' },
            ]}
          />
        </div>

        {visible.length === 0 ? (
          <p style={{ color: 'var(--couleur-encre-douce)', margin: 0, fontSize: '0.9rem' }}>
            {journal.length === 0
              ? 'Aucun mouvement. Confirmez un devis depuis Coter, ou saisissez un mouvement manuel.'
              : 'Aucun mouvement de ce type.'}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--espace-2)' }}>
            {[...visible].reverse().map(({ mouvement: m, snapshotApres }) => (
              <LigneMouvement key={m.id} m={m} apres={snapshotApres} onSupprimer={onSupprimer} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const titre = {
  fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)',
};

const boutonSecondaire = {
  padding: 'var(--espace-2) var(--espace-4)',
  border: '1px solid var(--couleur-trait-fort)', borderRadius: 'var(--rayon)',
  background: 'transparent', color: 'var(--couleur-encre)', fontSize: '0.85rem',
};

function Alertes({ levees }) {
  return (
    <div
      role="status"
      style={{
        padding: 'var(--espace-3)', border: `1px solid var(--couleur-alerte)`,
        borderRadius: 'var(--rayon)', background: 'var(--couleur-surface)',
        display: 'grid', gap: 'var(--espace-1)', fontSize: '0.85rem',
      }}
    >
      {levees.map((a, i) => (
        <p key={i} style={{ margin: 0, color: 'var(--couleur-alerte)' }}>
          {a.type === 'liquidite'
            ? <>Caisse {a.caisse} à découvert de <span className="chiffre">{cfa(a.manque, a.devise === 'EUR' ? '€' : a.devise)}</span>.</>
            : <>Vous portez <span className="chiffre">{eur(a.valeur)}</span> non revendus, au-delà de votre seuil.</>}
        </p>
      ))}
      {/* Le document 07 est explicite : on signale, on ne bloque jamais. */}
      <p style={{ margin: 0, color: 'var(--couleur-encre-douce)', fontSize: '0.8rem' }}>
        Signalé à titre indicatif. Aucune opération n’est bloquée.
      </p>
    </div>
  );
}

function Caisse({ libelle, valeur, sousSeuil }) {
  return (
    <div className="carte" style={{ padding: 'var(--espace-3)', borderColor: sousSeuil ? 'var(--couleur-alerte)' : undefined }}>
      <div className="etiquette">{libelle}</div>
      <div
        className="chiffre"
        style={{ fontSize: '1.05rem', fontWeight: 600, marginTop: 'var(--espace-1)', color: sousSeuil ? 'var(--couleur-alerte)' : undefined }}
      >
        {valeur}
      </div>
    </div>
  );
}

function Grandeur({ libelle, valeur, couleur }) {
  return (
    <div>
      <div className="etiquette">{libelle}</div>
      <div className="chiffre" style={{ fontSize: '1.05rem', fontWeight: 600, marginTop: 'var(--espace-1)', color: couleur }}>
        {valeur}
      </div>
    </div>
  );
}

const LIBELLE_TYPE = {
  achat_eur: 'Achat d’euros',
  vente_eur: 'Vente d’euros',
  frais_service: 'Frais de service',
  frais_operateur: 'Frais opérateur',
  depot_caisse: 'Dépôt de caisse',
  retrait_caisse: 'Retrait de caisse',
  ajustement: 'Ajustement',
};

function LigneMouvement({ m, apres, onSupprimer }) {
  const [ouvert, setOuvert] = useState(false);
  const entrant = m.type === 'vente_eur' || m.type === 'depot_caisse' || m.type === 'frais_service';

  return (
    <div className="carte" style={{ padding: 'var(--espace-3)' }}>
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
        style={{
          display: 'flex', width: '100%', gap: 'var(--espace-2)', alignItems: 'baseline',
          justifyContent: 'space-between', flexWrap: 'wrap',
          padding: 0, border: 'none', background: 'none', color: 'var(--couleur-encre)', textAlign: 'left',
        }}
      >
        <span>
          <strong style={{ fontSize: '0.9rem' }}>{LIBELLE_TYPE[m.type] ?? m.type}</strong>
          {m.corridor && m.corridor !== 'EUR' && (
            <span className="etiquette" style={{ marginLeft: 'var(--espace-2)' }}>{m.corridor}</span>
          )}
          <span className="chiffre" style={{ display: 'block', fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
            {String(m.date).slice(0, 10)}
            {m.taux > 0 && ` · ${fmtTaux(m.taux)} F/€`}
          </span>
        </span>
        <span className="chiffre" style={{ fontWeight: 600, color: entrant ? 'var(--couleur-accent)' : undefined }}>
          {m.montantEur ? eur(m.montantEur) : cfa(m.montantCfa, 'F')}
        </span>
      </button>

      {ouvert && (
        <div style={{ marginTop: 'var(--espace-3)', paddingTop: 'var(--espace-3)', borderTop: '1px solid var(--couleur-trait)' }}>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--espace-1) var(--espace-3)', margin: 0, fontSize: '0.85rem' }}>
            {m.montantEur > 0 && <><dt style={{ color: 'var(--couleur-encre-douce)' }}>Euros</dt><dd className="chiffre" style={{ margin: 0, textAlign: 'right' }}>{eur(m.montantEur)}</dd></>}
            {m.montantCfa !== 0 && <><dt style={{ color: 'var(--couleur-encre-douce)' }}>CFA</dt><dd className="chiffre" style={{ margin: 0, textAlign: 'right' }}>{cfa(m.montantCfa, 'F')}</dd></>}
            {m.note && <><dt style={{ color: 'var(--couleur-encre-douce)' }}>Note</dt><dd style={{ margin: 0, textAlign: 'right' }}>{m.note}</dd></>}
          </dl>

          {/* L'état du livre à la date du mouvement, figé à l'enregistrement :
              il permet de relire l'histoire sans rejouer toute la chaîne. */}
          {apres && (
            <p className="chiffre" style={{ margin: 'var(--espace-3) 0 0', fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
              Après : stock {eur(apres.stockEur)} · CMP {fmtTaux(apres.cmp)} · réalisé {cfa(apres.pnlRealiseCfa, 'F')}
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              if (window.confirm('Supprimer ce mouvement ?\n\nUn mouvement erroné se corrige normalement par un ajustement, qui garde les deux traces. Ne supprimez que ce qui a été créé par erreur.')) {
                onSupprimer(m.id);
              }
            }}
            style={{ ...boutonSecondaire, marginTop: 'var(--espace-3)', color: 'var(--couleur-alerte)', borderColor: 'var(--couleur-alerte)' }}
          >
            Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

const TYPES_MANUELS = TYPES.filter((t) => t === 'depot_caisse' || t === 'retrait_caisse' || t === 'ajustement');

function MouvementManuel({ onValider, onAnnuler }) {
  const [type, setType] = useState('depot_caisse');
  const [corridor, setCorridor] = useState('XAF');
  const [montant, setMontant] = useState('');
  const [pnl, setPnl] = useState('');
  const [note, setNote] = useState('');

  const enEuros = corridor === 'EUR';
  const valeur = Number.parseFloat(String(montant).replace(',', '.'));
  const valide = Number.isFinite(valeur) && valeur !== 0;

  return (
    <div className="carte" style={{ display: 'grid', gap: 'var(--espace-3)', marginTop: 'var(--espace-3)' }}>
      <div className="grille-2">
        <label style={{ display: 'block' }}>
          <span className="etiquette">Type</span>
          <select value={type} onChange={(e) => setType(e.target.value)} className="champ" style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}>
            {TYPES_MANUELS.map((t) => <option key={t} value={t}>{LIBELLE_TYPE[t]}</option>)}
          </select>
        </label>
        <label style={{ display: 'block' }}>
          <span className="etiquette">Caisse</span>
          <select value={corridor} onChange={(e) => setCorridor(e.target.value)} className="champ" style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}>
            <option value="EUR">Europe (EUR)</option>
            <option value="XAF">CEMAC (XAF)</option>
            <option value="XOF">UEMOA (XOF)</option>
          </select>
        </label>
      </div>

      <label style={{ display: 'block' }}>
        <span className="etiquette">Montant ({enEuros ? 'EUR' : corridor})</span>
        <input type="text" inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)} placeholder="0" className="champ" style={{ marginTop: 'var(--espace-1)' }} />
      </label>

      {type === 'ajustement' && (
        <label style={{ display: 'block' }}>
          <span className="etiquette">
            Effet sur la marge réalisée (CFA)
            <Info titre="Ajustement">
              Le seul mouvement qui déclare son effet sur le résultat au lieu de
              le déduire. Il ne touche <strong>jamais</strong> le stock : une
              erreur de position se corrige par un achat ou une vente
              compensatoire, pour que le coût moyen reste issu de flux réels.
            </Info>
          </span>
          <input type="text" inputMode="decimal" value={pnl} onChange={(e) => setPnl(e.target.value)} placeholder="0" className="champ" style={{ marginTop: 'var(--espace-1)' }} />
        </label>
      )}

      <label style={{ display: 'block' }}>
        <span className="etiquette">Note</span>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Apport de trésorerie" className="champ" style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }} />
      </label>

      <div style={{ display: 'flex', gap: 'var(--espace-2)', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={!valide}
          onClick={() => onValider({
            mouvement: {
              type,
              corridor,
              montantEur: enEuros ? valeur : 0,
              montantCfa: enEuros ? 0 : valeur,
              taux: 0,
              pnlCfa: type === 'ajustement' ? (Number.parseFloat(String(pnl).replace(',', '.')) || 0) : 0,
            },
            note,
          })}
          style={{
            padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
            background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
          }}
        >
          Enregistrer
        </button>
        <button type="button" onClick={onAnnuler} style={boutonSecondaire}>Annuler</button>
      </div>
    </div>
  );
}
